import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Play,
  FileText,
  Plus,
  Folder,
  ChevronRight,
  Search,
  Eye,
  Clock,
  Pencil,
  Trash2,
  X,
  Download,
  Upload,
  ExternalLink,
  GraduationCap,
  Sparkles,
  Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to extract info from URL
const parseUrlForMaterial = (url) => {
  try {
    // Decode URL
    const decodedUrl = decodeURIComponent(url);
    
    // Extract filename from URL
    const urlParts = decodedUrl.split('/');
    let filename = urlParts[urlParts.length - 1];
    
    // Remove query params
    filename = filename.split('?')[0];
    
    // Remove file ID prefix (like "bjr987ss_")
    filename = filename.replace(/^[a-z0-9]+_/i, '');
    
    // Get extension
    const extMatch = filename.match(/\.([a-zA-Z0-9]+)$/);
    const extension = extMatch ? extMatch[1].toLowerCase() : '';
    
    // Determine type
    const videoExtensions = ['mp4', 'mov', 'avi', 'webm', 'mkv'];
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'png', 'jpg', 'jpeg'];
    
    let materialType = 'document';
    if (videoExtensions.includes(extension)) {
      materialType = 'video';
    }
    
    // Clean up title (remove extension, replace special chars)
    let title = filename.replace(/\.[^/.]+$/, ''); // Remove extension
    title = title.replace(/[-_]/g, ' '); // Replace dashes/underscores with spaces
    title = title.replace(/\s+/g, ' ').trim(); // Normalize spaces
    
    return { title, materialType, extension };
  } catch (e) {
    return { title: '', materialType: 'document', extension: '' };
  }
};

export default function Training() {
  const { user, token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  
  // Dialog states
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentDocument, setCurrentDocument] = useState(null);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "", icon: "folder", order: 0 });
  const [materialForm, setMaterialForm] = useState({
    category_id: "",
    title: "",
    description: "",
    material_type: "video",
    url: "",
    duration: "",
    order: 0,
    tags: [],
  });
  
  // Bulk upload states
  const [bulkUrls, setBulkUrls] = useState("");
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [bulkParsedItems, setBulkParsedItems] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkGeneratingDescriptions, setBulkGeneratingDescriptions] = useState(false);
  const [bulkUploadMode, setBulkUploadMode] = useState("files"); // "files" or "urls"
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(""); // Status message during upload
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
  const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024; // 50MB - use chunked upload above this

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchMaterials(selectedCategory.id);
    } else {
      fetchAllMaterials();
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/training/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(res.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load training categories");
      setLoading(false);
    }
  };

  const fetchMaterials = async (categoryId) => {
    try {
      const res = await axios.get(`${API}/api/training/materials?category_id=${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMaterials(res.data);
    } catch (error) {
      toast.error("Failed to load training materials");
    }
  };

  const fetchAllMaterials = async () => {
    try {
      const res = await axios.get(`${API}/api/training/materials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMaterials(res.data);
    } catch (error) {
      toast.error("Failed to load training materials");
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/api/training/categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/api/training/categories`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Category created");
      }
      setShowCategoryDialog(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "", icon: "folder", order: 0 });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this category and all its materials?")) return;
    try {
      await axios.delete(`${API}/api/training/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Category deleted");
      if (selectedCategory?.id === categoryId) {
        setSelectedCategory(null);
      }
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const handleSaveMaterial = async () => {
    try {
      if (editingMaterial) {
        await axios.put(`${API}/api/training/materials/${editingMaterial.id}`, materialForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Material updated");
      } else {
        await axios.post(`${API}/api/training/materials`, materialForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Material added");
      }
      setShowMaterialDialog(false);
      setEditingMaterial(null);
      setMaterialForm({
        category_id: selectedCategory?.id || "",
        title: "",
        description: "",
        material_type: "video",
        url: "",
        duration: "",
        order: 0,
        tags: [],
      });
      if (selectedCategory) {
        fetchMaterials(selectedCategory.id);
      } else {
        fetchAllMaterials();
      }
      fetchCategories(); // Update counts
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save material");
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm("Delete this training material?")) return;
    try {
      await axios.delete(`${API}/api/training/materials/${materialId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Material deleted");
      if (selectedCategory) {
        fetchMaterials(selectedCategory.id);
      } else {
        fetchAllMaterials();
      }
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  // Bulk upload handlers
  const handleParseBulkUrls = () => {
    const urls = bulkUrls.split('\n').filter(url => url.trim());
    const parsed = urls.map((url, index) => {
      const trimmedUrl = url.trim();
      const info = parseUrlForMaterial(trimmedUrl);
      return {
        id: index,
        url: trimmedUrl,
        title: info.title,
        material_type: info.materialType,
        description: "",
        selected: true,
      };
    });
    setBulkParsedItems(parsed);
  };

  const handleBulkGenerateDescriptions = async () => {
    if (!bulkCategoryId) {
      toast.error("Please select a category first");
      return;
    }
    setBulkGeneratingDescriptions(true);
    const categoryName = categories.find(c => c.id === bulkCategoryId)?.name || '';
    
    const updated = [...bulkParsedItems];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].selected && updated[i].title) {
        try {
          const res = await axios.post(
            `${API}/api/training/generate-description`,
            {
              title: updated[i].title,
              material_type: updated[i].material_type,
              category_name: categoryName,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          updated[i].description = res.data.description;
        } catch (error) {
          console.error("Failed to generate description for", updated[i].title);
        }
      }
    }
    setBulkParsedItems(updated);
    setBulkGeneratingDescriptions(false);
    toast.success("Descriptions generated!");
  };

  const handleBulkUpload = async () => {
    if (!bulkCategoryId) {
      toast.error("Please select a category");
      return;
    }
    
    const itemsToUpload = bulkParsedItems.filter(item => item.selected && item.title && item.url);
    if (itemsToUpload.length === 0) {
      toast.error("No items to upload");
      return;
    }
    
    setBulkUploading(true);
    let successCount = 0;
    
    for (const item of itemsToUpload) {
      try {
        await axios.post(
          `${API}/api/training/materials`,
          {
            category_id: bulkCategoryId,
            title: item.title,
            description: item.description || "",
            material_type: item.material_type,
            url: item.url,
            order: successCount,
            tags: [],
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        successCount++;
      } catch (error) {
        console.error("Failed to upload", item.title);
      }
    }
    
    setBulkUploading(false);
    toast.success(`Uploaded ${successCount} of ${itemsToUpload.length} materials!`);
    setShowBulkUploadDialog(false);
    setBulkUrls("");
    setBulkParsedItems([]);
    setBulkCategoryId("");
    fetchCategories();
    if (selectedCategory) {
      fetchMaterials(selectedCategory.id);
    } else {
      fetchAllMaterials();
    }
  };

  // Upload a single file using chunked upload
  const uploadFileChunked = async (file, categoryId) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    // Initialize upload
    setUploadStatus(`Initializing upload for ${file.name}...`);
    const initRes = await axios.post(
      `${API}/api/training/chunked-upload/init`,
      {
        filename: file.name,
        file_size: file.size,
        category_id: categoryId,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const uploadId = initRes.data.upload_id;
    
    // Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      const chunkFormData = new FormData();
      chunkFormData.append("upload_id", uploadId);
      chunkFormData.append("chunk_index", i);
      chunkFormData.append("chunk", chunk);
      
      await axios.post(
        `${API}/api/training/chunked-upload/chunk`,
        chunkFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      // Update progress
      const chunkProgress = Math.round(((i + 1) / totalChunks) * 100);
      setUploadProgress(chunkProgress);
      setUploadStatus(`Uploading ${file.name}: ${chunkProgress}% (chunk ${i + 1}/${totalChunks})`);
    }
    
    // Complete upload
    setUploadStatus(`Finalizing ${file.name}...`);
    const completeRes = await axios.post(
      `${API}/api/training/chunked-upload/complete`,
      {
        upload_id: uploadId,
        filename: file.name,
        category_id: categoryId,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    return completeRes.data.material;
  };

  // Upload a single file using regular upload (for small files)
  const uploadFileRegular = async (file, categoryId) => {
    const formData = new FormData();
    formData.append("category_id", categoryId);
    formData.append("files", file);
    
    const res = await axios.post(`${API}/api/training/upload-files`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      timeout: 120000,
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
        setUploadStatus(`Uploading ${file.name}: ${percent}%`);
      },
    });
    
    if (res.data.successful > 0) {
      return res.data.results[0].material;
    }
    throw new Error("Upload failed");
  };

  // Handle actual file upload
  const handleFileUpload = async () => {
    if (!bulkCategoryId) {
      toast.error("Please select a category");
      return;
    }
    if (selectedFiles.length === 0) {
      toast.error("Please select files to upload");
      return;
    }

    setBulkUploading(true);
    setUploadProgress(0);
    setUploadStatus("");
    setCurrentFileIndex(0);

    const uploadedMaterials = [];
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setCurrentFileIndex(i);
      setUploadProgress(0);
      
      try {
        let material;
        
        // Use chunked upload for large files
        if (file.size > LARGE_FILE_THRESHOLD) {
          setUploadStatus(`Large file detected (${(file.size / 1024 / 1024).toFixed(1)}MB) - using chunked upload...`);
          material = await uploadFileChunked(file, bulkCategoryId);
        } else {
          material = await uploadFileRegular(file, bulkCategoryId);
        }
        
        uploadedMaterials.push(material);
        successCount++;
        toast.success(`Uploaded: ${file.name}`);
        
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        failCount++;
        // Show more detailed error message
        const errorMsg = error.response?.data?.detail || error.message || "Unknown error";
        toast.error(`Failed: ${file.name} - ${errorMsg}`);
      }
    }

    // Show final results
    if (successCount > 0) {
      toast.success(`Completed: ${successCount} uploaded, ${failCount} failed`);
      
      // Offer to generate descriptions
      setBulkParsedItems(uploadedMaterials.map((m, i) => ({
        ...m,
        id: i,
        selected: true,
      })));
    }
    
    setSelectedFiles([]);
    setUploadStatus("");
    fetchCategories();
    if (selectedCategory) {
      fetchMaterials(selectedCategory.id);
    } else {
      fetchAllMaterials();
    }

    setBulkUploading(false);
    setUploadProgress(0);
  };

  const openVideo = (material) => {
    // For local uploads, prepend the API URL
    let videoMaterial = { ...material };
    if (videoMaterial.url.startsWith('/api/')) {
      videoMaterial.url = `${API}${videoMaterial.url}`;
    }
    setCurrentVideo(videoMaterial);
    setShowVideoPlayer(true);
    // Increment view count
    axios.get(`${API}/api/training/materials/${material.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const openDocument = (material) => {
    // For local uploads, prepend the API URL
    let url = material.url;
    if (url.startsWith('/api/')) {
      url = `${API}${url}`;
    }
    
    // Get file extension
    const ext = url.split('.').pop()?.toLowerCase() || '';
    
    // Viewable document types
    const officeTypes = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    const pdfTypes = ['pdf'];
    const imageTypes = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
    
    if (officeTypes.includes(ext) || pdfTypes.includes(ext) || imageTypes.includes(ext)) {
      // Show in-app viewer
      setCurrentDocument({ ...material, url, ext });
      setShowDocumentViewer(true);
    } else {
      // Download other file types
      window.open(url, "_blank");
    }
    
    // Increment view count
    axios.get(`${API}/api/training/materials/${material.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  // Get viewer URL for documents
  const getDocumentViewerUrl = (url, ext) => {
    const encodedUrl = encodeURIComponent(url);
    
    // Office documents - use Microsoft Office Online viewer
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
    }
    
    // PDFs and images - display directly
    return url;
  };

  const filteredMaterials = materials.filter(
    (m) =>
      m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.tags?.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="training-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <GraduationCap className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Training Center</h1>
            <p className="text-muted-foreground text-sm">Videos and documents to help you succeed</p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setCategoryForm({ name: "", description: "", icon: "folder", order: categories.length });
                setEditingCategory(null);
                setShowCategoryDialog(true);
              }}
              data-testid="add-category-btn"
            >
              <Folder className="h-4 w-4 mr-2" />
              Add Category
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setBulkCategoryId(selectedCategory?.id || (categories[0]?.id || ""));
                setBulkUrls("");
                setBulkParsedItems([]);
                setShowBulkUploadDialog(true);
              }}
              data-testid="bulk-upload-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Bulk Upload
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/api/training/export`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `training-export-${new Date().toISOString().split('T')[0]}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Training data exported!");
                } catch (error) {
                  toast.error("Failed to export training data");
                }
              }}
              data-testid="export-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.json';
                input.onchange = async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  try {
                    const text = await file.text();
                    const data = JSON.parse(text);
                    const res = await axios.post(`${API}/api/training/import`, data, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    toast.success(`Imported: ${res.data.categories_added} categories, ${res.data.materials_added} materials`);
                    fetchCategories();
                    fetchAllMaterials();
                  } catch (error) {
                    toast.error("Failed to import training data");
                  }
                };
                input.click();
              }}
              data-testid="import-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button
              onClick={() => {
                setMaterialForm({
                  category_id: selectedCategory?.id || (categories[0]?.id || ""),
                  title: "",
                  description: "",
                  material_type: "video",
                  url: "",
                  duration: "",
                  order: materials.length,
                  tags: [],
                });
                setEditingMaterial(null);
                setShowMaterialDialog(true);
              }}
              data-testid="add-material-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Material
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-6">
        {/* Sidebar - Categories */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-foreground">Categories</h3>
            </div>
            <div className="p-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                  !selectedCategory
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-muted text-muted-foreground hover:text-foreground"
                }`}
                data-testid="all-materials-btn"
              >
                <span className="flex items-center gap-2">
                  <Folder className="h-4 w-4" />
                  All Materials
                </span>
                <span className="text-xs bg-muted px-2 py-0.5 rounded-full">
                  {materials.length}
                </span>
              </button>

              {categories.map((category) => (
                <div
                  key={category.id}
                  className={`group flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    selectedCategory?.id === category.id
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <button
                    onClick={() => setSelectedCategory(category)}
                    className="flex-1 flex items-center gap-2 text-left"
                    data-testid={`category-${category.id}`}
                  >
                    <Folder className="h-4 w-4" />
                    <span className="truncate">{category.name}</span>
                  </button>
                  <div className="flex items-center gap-1">
                    <span className="text-xs bg-muted px-2 py-0.5 rounded-full">
                      {category.material_count}
                    </span>
                    {isAdmin && (
                      <div className="hidden group-hover:flex items-center gap-1 ml-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCategoryForm({
                              name: category.name,
                              description: category.description,
                              icon: category.icon,
                              order: category.order,
                            });
                            setEditingCategory(category);
                            setShowCategoryDialog(true);
                          }}
                          className="p-1 hover:bg-muted rounded"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(category.id);
                          }}
                          className="p-1 hover:bg-red-500/20 text-red-500 rounded"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search training materials..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-training"
              />
            </div>
          </div>

          {/* Category Header */}
          {selectedCategory && (
            <div className="mb-4 p-4 bg-primary/5 rounded-xl border border-primary/20">
              <h2 className="text-lg font-semibold text-foreground">{selectedCategory.name}</h2>
              {selectedCategory.description && (
                <p className="text-sm text-muted-foreground mt-1">{selectedCategory.description}</p>
              )}
            </div>
          )}

          {/* Materials Grid */}
          {filteredMaterials.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <GraduationCap className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No training materials found</p>
              {isAdmin && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setMaterialForm({
                      category_id: selectedCategory?.id || (categories[0]?.id || ""),
                      title: "",
                      description: "",
                      material_type: "video",
                      url: "",
                      duration: "",
                      order: 0,
                      tags: [],
                    });
                    setShowMaterialDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Material
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMaterials.map((material) => (
                <div
                  key={material.id}
                  className="group bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-all duration-200 hover:border-primary/50"
                  data-testid={`material-${material.id}`}
                >
                  {/* Thumbnail/Preview */}
                  <div
                    className="relative h-36 bg-muted flex items-center justify-center cursor-pointer"
                    onClick={() =>
                      material.material_type === "video"
                        ? openVideo(material)
                        : openDocument(material)
                    }
                  >
                    {material.material_type === "video" ? (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20" />
                        <div className="w-14 h-14 rounded-full bg-card flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <Play className="h-6 w-6 text-blue-500 ml-1" />
                        </div>
                        {material.duration && (
                          <span className="absolute bottom-2 right-2 text-xs bg-black/70 text-white px-2 py-1 rounded">
                            {material.duration}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-teal-500/20" />
                        <FileText className="h-12 w-12 text-emerald-500" />
                      </>
                    )}}
                    
                    {/* Type badge */}
                    <span
                      className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-full font-medium ${
                        material.material_type === "video"
                          ? "bg-blue-500/20 text-blue-700"
                          : "bg-emerald-500/20 text-emerald-700"
                      }`}
                    >
                      {material.material_type === "video" ? "Video" : "Document"}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground mb-1 line-clamp-2">{material.title}</h3>
                    {material.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{material.description}</p>
                    )}
                    
                    {!selectedCategory && (
                      <p className="text-xs text-muted-foreground mb-2">
                        <Folder className="h-3 w-3 inline mr-1" />
                        {getCategoryName(material.category_id)}
                      </p>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {material.view_count} views
                      </span>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            material.material_type === "video"
                              ? openVideo(material)
                              : openDocument(material)
                          }
                        >
                          {material.material_type === "video" ? (
                            <>
                              <Play className="h-4 w-4 mr-1" /> Watch
                            </>
                          ) : (
                            <>
                              <ExternalLink className="h-4 w-4 mr-1" /> View
                            </>
                          )}
                        </Button>
                        
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => {
                                setMaterialForm({
                                  category_id: material.category_id,
                                  title: material.title,
                                  description: material.description,
                                  material_type: material.material_type,
                                  url: material.url,
                                  duration: material.duration || "",
                                  order: material.order,
                                  tags: material.tags || [],
                                });
                                setEditingMaterial(material);
                                setShowMaterialDialog(true);
                              }}
                              className="p-2 hover:bg-muted rounded"
                            >
                              <Pencil className="h-4 w-4 text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => handleDeleteMaterial(material.id)}
                              className="p-2 hover:bg-red-500/10 rounded"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit Category" : "Add Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="cat-name">Category Name</Label>
              <Input
                id="cat-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Firm 360"
              />
            </div>
            <div>
              <Label htmlFor="cat-desc">Description</Label>
              <Textarea
                id="cat-desc"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Brief description of this category"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} disabled={!categoryForm.name}>
              {editingCategory ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Material Dialog */}
      <Dialog open={showMaterialDialog} onOpenChange={setShowMaterialDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingMaterial ? "Edit Material" : "Add Training Material"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select
                value={materialForm.category_id}
                onValueChange={(v) => setMaterialForm({ ...materialForm, category_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="mat-url">URL</Label>
              <Input
                id="mat-url"
                value={materialForm.url}
                onChange={(e) => {
                  const url = e.target.value;
                  setMaterialForm({ ...materialForm, url });
                  
                  // Auto-parse URL for title and type
                  if (url && url.startsWith('http')) {
                    const parsed = parseUrlForMaterial(url);
                    if (parsed.title && !materialForm.title) {
                      setMaterialForm(prev => ({
                        ...prev,
                        url,
                        title: parsed.title,
                        material_type: parsed.materialType,
                      }));
                    } else {
                      setMaterialForm(prev => ({
                        ...prev,
                        url,
                        material_type: parsed.materialType,
                      }));
                    }
                  }
                }}
                placeholder="Paste URL here - title and type will auto-fill"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paste a URL and title/type will be auto-detected
              </p>
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={materialForm.material_type}
                onValueChange={(v) => setMaterialForm({ ...materialForm, material_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="mat-title">Title</Label>
              <Input
                id="mat-title"
                value={materialForm.title}
                onChange={(e) => setMaterialForm({ ...materialForm, title: e.target.value })}
                placeholder="Auto-filled from URL, or enter manually"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="mat-desc">Description</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-blue-500 hover:text-blue-700"
                  disabled={!materialForm.title || generatingDescription}
                  onClick={async () => {
                    if (!materialForm.title) return;
                    setGeneratingDescription(true);
                    try {
                      const categoryName = categories.find(c => c.id === materialForm.category_id)?.name || '';
                      const res = await axios.post(
                        `${API}/api/training/generate-description`,
                        {
                          title: materialForm.title,
                          material_type: materialForm.material_type,
                          category_name: categoryName,
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      setMaterialForm({ ...materialForm, description: res.data.description });
                      toast.success("Description generated!");
                    } catch (error) {
                      toast.error("Failed to generate description");
                    }
                    setGeneratingDescription(false);
                  }}
                >
                  {generatingDescription ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="h-3 w-3 mr-1" /> Generate with AI</>
                  )}
                </Button>
              </div>
              <Textarea
                id="mat-desc"
                value={materialForm.description}
                onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })}
                placeholder="Click 'Generate with AI' or enter manually"
                rows={3}
              />
            </div>
            {materialForm.material_type === "video" && (
              <div>
                <Label htmlFor="mat-duration">Duration (optional)</Label>
                <Input
                  id="mat-duration"
                  value={materialForm.duration}
                  onChange={(e) => setMaterialForm({ ...materialForm, duration: e.target.value })}
                  placeholder="e.g., 5:30"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaterialDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveMaterial}
              disabled={!materialForm.category_id || !materialForm.title || !materialForm.url}
            >
              {editingMaterial ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Player Dialog */}
      <Dialog open={showVideoPlayer} onOpenChange={setShowVideoPlayer}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <div className="relative">
            <button
              onClick={() => setShowVideoPlayer(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white"
            >
              <X className="h-5 w-5" />
            </button>
            {currentVideo && (
              <div>
                <video
                  src={currentVideo.url}
                  controls
                  autoPlay
                  preload="auto"
                  className="w-full max-h-[70vh]"
                  data-testid="video-player"
                  onError={(e) => {
                    console.error("Video error:", e.target.error);
                    toast.error(`Video failed to load: ${e.target.error?.message || 'Unknown error'}`);
                  }}
                  onLoadStart={() => console.log("Video loading started:", currentVideo.url)}
                  onCanPlay={() => console.log("Video can play")}
                  onWaiting={() => console.log("Video waiting/buffering")}
                >
                  Your browser does not support the video tag.
                </video>
                <div className="p-4 bg-card">
                  <h3 className="font-semibold text-lg text-foreground">{currentVideo.title}</h3>
                  {currentVideo.description && (
                    <p className="text-muted-foreground mt-2">{currentVideo.description}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog open={showDocumentViewer} onOpenChange={setShowDocumentViewer}>
        <DialogContent className="max-w-5xl h-[85vh] p-0 overflow-hidden">
          <div className="relative h-full flex flex-col">
            <div className="flex items-center justify-between p-4 border-b bg-card">
              <div>
                <h3 className="font-semibold text-lg text-foreground">{currentDocument?.title}</h3>
                {currentDocument?.description && (
                  <p className="text-sm text-muted-foreground mt-1">{currentDocument.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(currentDocument?.url, "_blank")}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <button
                  onClick={() => setShowDocumentViewer(false)}
                  className="p-2 hover:bg-muted rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            {currentDocument && (
              <div className="flex-1 bg-white">
                {['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(currentDocument.ext) ? (
                  <iframe
                    src={getDocumentViewerUrl(currentDocument.url, currentDocument.ext)}
                    className="w-full h-full border-0"
                    title={currentDocument.title}
                  />
                ) : currentDocument.ext === 'pdf' ? (
                  <iframe
                    src={currentDocument.url}
                    className="w-full h-full border-0"
                    title={currentDocument.title}
                  />
                ) : ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(currentDocument.ext) ? (
                  <div className="w-full h-full flex items-center justify-center p-4">
                    <img
                      src={currentDocument.url}
                      alt={currentDocument.title}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <p className="text-muted-foreground">Preview not available for this file type</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUploadDialog} onOpenChange={setShowBulkUploadDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Bulk Upload Training Materials</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-y-auto">
            {/* Category Selection */}
            <div>
              <Label>Select Category</Label>
              <Select
                value={bulkCategoryId}
                onValueChange={(v) => setBulkCategoryId(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a category for all items" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mode Toggle */}
            <div className="flex gap-2">
              <Button
                variant={bulkUploadMode === "files" ? "default" : "outline"}
                size="sm"
                onClick={() => setBulkUploadMode("files")}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Files
              </Button>
              <Button
                variant={bulkUploadMode === "urls" ? "default" : "outline"}
                size="sm"
                onClick={() => setBulkUploadMode("urls")}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Paste URLs
              </Button>
            </div>

            {bulkUploadMode === "files" ? (
              /* File Upload Mode */
              <div>
                {selectedFiles.length === 0 && bulkParsedItems.length === 0 ? (
                  <div
                    className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-500/10/50 transition-colors"
                    onClick={() => document.getElementById('bulk-file-input').click()}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const files = Array.from(e.dataTransfer.files);
                      setSelectedFiles(files);
                    }}
                  >
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground font-medium">Drop files here or click to browse</p>
                    <p className="text-sm text-muted-foreground mt-1">Videos (mp4, mov) and Documents (pdf, docx, xlsx)</p>
                    <input
                      id="bulk-file-input"
                      type="file"
                      multiple
                      accept=".mp4,.mov,.avi,.webm,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                      className="hidden"
                      onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                    />
                  </div>
                ) : selectedFiles.length > 0 ? (
                  /* Show selected files before upload */
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>{selectedFiles.length} files selected</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedFiles([])}
                      >
                        Clear
                      </Button>
                    </div>
                    <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="p-2 flex items-center gap-3">
                          {file.type.startsWith('video/') ? (
                            <Play className="h-4 w-4 text-blue-500" />
                          ) : (
                            <FileText className="h-4 w-4 text-emerald-500" />
                          )}
                          <span className="text-sm flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {(file.size / 1024 / 1024).toFixed(1)} MB
                          </span>
                        </div>
                      ))}
                    </div>
                    {bulkUploading && (
                      <div className="mt-3">
                        {selectedFiles.length > 1 && (
                          <p className="text-sm text-muted-foreground mb-2 text-center font-medium">
                            File {currentFileIndex + 1} of {selectedFiles.length}
                          </p>
                        )}
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-600 transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 text-center">
                          {uploadStatus || `${uploadProgress}% uploaded`}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Show uploaded items for description generation */
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Files uploaded! Generate descriptions?</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBulkGenerateDescriptions}
                        disabled={bulkGeneratingDescriptions || !bulkCategoryId}
                      >
                        {bulkGeneratingDescriptions ? (
                          <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating...</>
                        ) : (
                          <><Sparkles className="h-3 w-3 mr-1" /> Generate All Descriptions</>
                        )}
                      </Button>
                    </div>
                    <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                      {bulkParsedItems.map((item, index) => (
                        <div key={index} className="p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              item.material_type === 'video' 
                                ? 'bg-blue-500/20 text-blue-700' 
                                : 'bg-emerald-500/20 text-emerald-700'
                            }`}>
                              {item.material_type}
                            </span>
                            <span className="font-medium text-sm">{item.title}</span>
                          </div>
                          <Input
                            value={item.description || ""}
                            onChange={(e) => {
                              const updated = [...bulkParsedItems];
                              updated[index].description = e.target.value;
                              setBulkParsedItems(updated);
                            }}
                            className="h-8 text-sm"
                            placeholder="Description (click Generate All above)"
                          />
                        </div>
                      ))}
                    </div>
                    <Button
                      className="mt-3 w-full"
                      onClick={async () => {
                        // Update descriptions in database
                        for (const item of bulkParsedItems) {
                          if (item.description) {
                            try {
                              await axios.put(
                                `${API}/api/training/materials/${item.id}`,
                                { ...item, category_id: bulkCategoryId },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                            } catch (e) {
                              console.error("Failed to update", item.title);
                            }
                          }
                        }
                        toast.success("Descriptions saved!");
                        setShowBulkUploadDialog(false);
                        setBulkParsedItems([]);
                        fetchCategories();
                        if (selectedCategory) {
                          fetchMaterials(selectedCategory.id);
                        } else {
                          fetchAllMaterials();
                        }
                      }}
                    >
                      Save Descriptions & Close
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              /* URL Mode */
              <div>
                {bulkParsedItems.length === 0 ? (
                  <div>
                    <Label>Paste URLs (one per line)</Label>
                    <Textarea
                      value={bulkUrls}
                      onChange={(e) => setBulkUrls(e.target.value)}
                      placeholder="https://sharepoint.com/video1.mp4
https://sharepoint.com/document1.pdf
https://sharepoint.com/video2.mp4"
                      rows={8}
                      className="font-mono text-sm"
                    />
                    <Button 
                      onClick={handleParseBulkUrls} 
                      className="mt-2"
                      disabled={!bulkUrls.trim()}
                    >
                      Parse URLs
                    </Button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Review Items ({bulkParsedItems.filter(i => i.selected).length} selected)</Label>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setBulkParsedItems([]);
                            setBulkUrls("");
                          }}
                        >
                          Start Over
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleBulkGenerateDescriptions}
                          disabled={bulkGeneratingDescriptions || !bulkCategoryId}
                        >
                          {bulkGeneratingDescriptions ? (
                            <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating...</>
                          ) : (
                            <><Sparkles className="h-3 w-3 mr-1" /> Generate All Descriptions</>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="border border-border rounded-lg divide-y divide-border max-h-[400px] overflow-y-auto">
                      {bulkParsedItems.map((item, index) => (
                        <div key={item.id} className={`p-3 ${item.selected ? 'bg-card' : 'bg-muted opacity-60'}`}>
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={item.selected}
                              onChange={(e) => {
                                const updated = [...bulkParsedItems];
                                updated[index].selected = e.target.checked;
                                setBulkParsedItems(updated);
                              }}
                              className="mt-1"
                            />
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  item.material_type === 'video' 
                                    ? 'bg-blue-500/20 text-blue-500' 
                                    : 'bg-emerald-500/20 text-emerald-500'
                                }`}>
                                  {item.material_type}
                                </span>
                                <Input
                                  value={item.title}
                                  onChange={(e) => {
                                    const updated = [...bulkParsedItems];
                                    updated[index].title = e.target.value;
                                    setBulkParsedItems(updated);
                                  }}
                                  className="h-8 text-sm font-medium"
                                  placeholder="Title"
                                />
                              </div>
                              <Input
                                value={item.description}
                                onChange={(e) => {
                                  const updated = [...bulkParsedItems];
                                  updated[index].description = e.target.value;
                                  setBulkParsedItems(updated);
                                }}
                                className="h-8 text-sm text-muted-foreground"
                                placeholder="Description (optional)"
                              />
                              <p className="text-xs text-muted-foreground truncate">{item.url}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => {
              setShowBulkUploadDialog(false);
              setSelectedFiles([]);
              setBulkParsedItems([]);
              setBulkUrls("");
            }}>
              Cancel
            </Button>
            {bulkUploadMode === "files" && selectedFiles.length > 0 && (
              <Button
                onClick={handleFileUpload}
                disabled={bulkUploading || !bulkCategoryId}
              >
                {bulkUploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <>Upload {selectedFiles.length} Files</>
                )}
              </Button>
            )}
            {bulkUploadMode === "urls" && bulkParsedItems.length > 0 && (
              <Button
                onClick={handleBulkUpload}
                disabled={bulkUploading || bulkParsedItems.filter(i => i.selected).length === 0 || !bulkCategoryId}
              >
                {bulkUploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <>Upload {bulkParsedItems.filter(i => i.selected).length} Items</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
